import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { 
  Mail, Eye, MousePointerClick, TrendingUp, RefreshCw, 
  BarChart3, Clock, CheckCircle2, XCircle
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';

interface OutreachEmail {
  id: string;
  tracking_id: string;
  recipient: string;
  website_name: string;
  subject: string;
  sent_at: string;
  opened_at: string | null;
  clicked_at: string | null;
  open_count: number;
  click_count: number;
  status: 'sent' | 'opened' | 'clicked' | 'bounced';
}

interface OutreachAnalyticsDashboardProps {
  userId: string;
}

export const OutreachAnalyticsDashboard = ({ userId }: OutreachAnalyticsDashboardProps) => {
  const [emails, setEmails] = useState<OutreachEmail[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalSent: 0,
    totalOpened: 0,
    totalClicked: 0,
    openRate: 0,
    clickRate: 0,
    avgOpenTime: 0,
  });

  useEffect(() => {
    fetchOutreachAnalytics();

    // Real-time subscription
    const channel = supabase
      .channel('outreach_analytics_realtime')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'email_analytics' },
        () => fetchOutreachAnalytics()
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [userId]);

  const fetchOutreachAnalytics = async () => {
    try {
      // Fetch backlinks with outreach data
      const { data: backlinks, error: backlinksError } = await supabase
        .from('backlinks')
        .select('*')
        .eq('agent_id', userId)
        .in('outreach_status', ['contacted', 'follow_up', 'connected'])
        .order('last_outreach_date', { ascending: false });

      if (backlinksError) throw backlinksError;

      // Fetch email analytics
      const { data: analytics, error: analyticsError } = await supabase
        .from('email_analytics')
        .select('*')
        .order('sent_at', { ascending: false })
        .limit(100);

      if (analyticsError) throw analyticsError;

      // Map backlinks to email format
      const emailData: OutreachEmail[] = (backlinks || []).map((b, idx) => ({
        id: b.id,
        tracking_id: `outreach_${b.id}`,
        recipient: b.contact_email || '',
        website_name: b.website_name,
        subject: `Outreach to ${b.website_name}`,
        sent_at: b.last_outreach_date || b.created_at,
        opened_at: analytics?.[idx]?.opened_at || null,
        clicked_at: analytics?.[idx]?.clicked_at || null,
        open_count: analytics?.[idx]?.open_count || 0,
        click_count: analytics?.[idx]?.click_count || 0,
        status: b.outreach_status === 'connected' ? 'clicked' : 
               (analytics?.[idx]?.opened_at ? 'opened' : 'sent'),
      }));

      setEmails(emailData);

      // Calculate stats
      const totalSent = emailData.length;
      const totalOpened = emailData.filter(e => e.opened_at).length;
      const totalClicked = emailData.filter(e => e.clicked_at).length;

      setStats({
        totalSent,
        totalOpened,
        totalClicked,
        openRate: totalSent > 0 ? (totalOpened / totalSent) * 100 : 0,
        clickRate: totalSent > 0 ? (totalClicked / totalSent) * 100 : 0,
        avgOpenTime: 24, // Placeholder
      });
    } catch (error) {
      console.error('Error fetching outreach analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  // Generate trend data for last 7 days
  const trendData = Array.from({ length: 7 }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() - (6 - i));
    const dateStr = date.toLocaleDateString('en-US', { weekday: 'short' });
    
    const dayEmails = emails.filter(e => {
      const sentDate = new Date(e.sent_at);
      return sentDate.toDateString() === date.toDateString();
    });

    return {
      date: dateStr,
      sent: dayEmails.length,
      opened: dayEmails.filter(e => e.opened_at).length,
      clicked: dayEmails.filter(e => e.clicked_at).length,
    };
  });

  const getStatusBadge = (email: OutreachEmail) => {
    if (email.clicked_at) {
      return <Badge className="bg-green-500"><MousePointerClick className="h-3 w-3 mr-1" />Clicked</Badge>;
    }
    if (email.opened_at) {
      return <Badge className="bg-blue-500"><Eye className="h-3 w-3 mr-1" />Opened ({email.open_count}x)</Badge>;
    }
    return <Badge variant="outline"><Mail className="h-3 w-3 mr-1" />Sent</Badge>;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center text-muted-foreground">Loading analytics...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="h-6 w-6" />
            Outreach Analytics
          </h2>
          <p className="text-muted-foreground">Real-time email open and click tracking</p>
        </div>
        <Button variant="outline" onClick={fetchOutreachAnalytics}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-muted-foreground" />
              <div>
                <div className="text-2xl font-bold">{stats.totalSent}</div>
                <p className="text-xs text-muted-foreground">Total Sent</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-blue-500" />
              <div>
                <div className="text-2xl font-bold text-blue-500">{stats.totalOpened}</div>
                <p className="text-xs text-muted-foreground">Opened</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <MousePointerClick className="h-5 w-5 text-green-500" />
              <div>
                <div className="text-2xl font-bold text-green-500">{stats.totalClicked}</div>
                <p className="text-xs text-muted-foreground">Clicked</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              <div>
                <div className="text-2xl font-bold">{stats.openRate.toFixed(1)}%</div>
                <p className="text-xs text-muted-foreground">Open Rate</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-accent" />
              <div>
                <div className="text-2xl font-bold">{stats.clickRate.toFixed(1)}%</div>
                <p className="text-xs text-muted-foreground">Click Rate</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Trend Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">7-Day Activity Trend</CardTitle>
          <CardDescription>Email engagement over the past week</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} className="fill-muted-foreground" />
                <YAxis tick={{ fontSize: 12 }} className="fill-muted-foreground" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--background))', 
                    border: '1px solid hsl(var(--border))' 
                  }}
                />
                <Area type="monotone" dataKey="sent" stackId="1" stroke="hsl(var(--muted-foreground))" fill="hsl(var(--muted))" name="Sent" />
                <Area type="monotone" dataKey="opened" stackId="2" stroke="hsl(var(--primary))" fill="hsl(var(--primary) / 0.3)" name="Opened" />
                <Area type="monotone" dataKey="clicked" stackId="3" stroke="hsl(142 76% 36%)" fill="hsl(142 76% 36% / 0.3)" name="Clicked" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Recent Emails Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Recent Outreach Activity</CardTitle>
          <CardDescription>Track engagement for each sent email</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border max-h-[400px] overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Website</TableHead>
                  <TableHead>Recipient</TableHead>
                  <TableHead>Sent</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-center">Opens</TableHead>
                  <TableHead className="text-center">Clicks</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {emails.slice(0, 20).map((email) => (
                  <TableRow key={email.id}>
                    <TableCell className="font-medium">{email.website_name}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {email.recipient || '-'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {new Date(email.sent_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-center">
                      {getStatusBadge(email)}
                    </TableCell>
                    <TableCell className="text-center">
                      {email.open_count > 0 ? (
                        <Badge variant="secondary">{email.open_count}</Badge>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {email.click_count > 0 ? (
                        <Badge variant="secondary">{email.click_count}</Badge>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
